// Раздел «Правила»: каталог норм Правил рыболовства поверх карты.
//
// Карта во всю ширину, лист с фильтрами и нормами плавает слева (`RulesSheet`),
// рубежи — тумблерами справа внизу. Каталог один (`src/rules/`); раздел,
// документ сверки и карточка точки — его представления.
//
// ЛЮБОЙ КЛИК ПО КАРТЕ СТАВИТ ТОЧКУ (решение владельца 17.10.2026). Точка ставит
// только свойства МЕСТА — район, места запретов, глубину, берег; вид, орудие и
// судно остаются выбором человека. Свойства берутся в окрестности клика
// (`factScale.clickRadius`, прижимает сервер), и спорное — у межи, у края места,
// у порога глубины — не ставится, а даёт «зависит от точного места».
//
// Карта своя (как у «Буёв»), поэтому рубежи — общим хуком и общими тумблерами,
// а координатная сетка — общим `MapGraticule`. Лист перетаскивается
// (`map/floatingPanel.ts`, как в «Квотах»): высокий лист закрывает треть карты.

import { domainMaxBounds } from '../domain/limits'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import type { BBox } from '../api'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { createStyleGate } from '../map/styleGate'
import { useMapView } from '../map/mapView'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { MapGraticule } from '../map/MapGraticule'
import { useFloatingPanel } from '../map/floatingPanel'
import { useWide } from '../map/useWide'
import { boxKey, boundsToBox, useBoundaryOverlays } from '../map/useBoundaryOverlays'
import { useLang, useT } from '../i18n'
import { useRegionScope } from '../species/useRegionScope'
import type { MapView } from '../map/mapView'
import { coverageNote } from './coverage'
import { RULES_URL_CLEARED, isRegionForbidden, parseRulesUrl, writeRulesUrl } from './rulesUrl'
import { homeOf, switchCatalog } from './catalogSwitch'
import type { RulesUrlState } from './rulesUrl'
import {
  POINT_COLOR, RULES_LAYER_IDS, applyPointLayer, applyRulesLayer, circlePolygon, featureBounds,
} from './rulesLayer'
import { clickRadius } from './factScale'
import { FilterChips } from './FilterChips'
import { PointFacts } from './PointFacts'
import { RuleGroups } from './RuleGroups'
import type { GroupJump } from './RuleGroups'
import { RulesSheet, useSheetCollapsed } from './RulesSheet'
import { sheetPadding } from './sheetPadding'

const HOME_CENTER: [number, number] = [150, 50]
const HOME_ZOOM = 3.4
const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
/** Окрестность без ответа сервера — ячейка GEBCO (`point_facts.GEBCO_CELL_M`). */
const FALLBACK_RADIUS_M = 463

/** Поиск уходит на сервер, отстоявшись: запрос на каждую букву незачем. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

const PANEL_HOME = { x: 12, y: 12 }

/** Карта строится, когда известен район домена: иначе Selara на миг открывала
 *  бы Курилы (тот же довод, что у `regionHome.viewToOpen`). */
export function RulesPage() {
  const scope = useRegionScope()
  if (!scope.known) return <div className="h-full w-full" />
  return <RulesPageInner home={scope.home} />
}

function RulesPageInner({ home }: { home: MapView | null }) {
  const t = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, setState] = useState<RulesUrlState>(() => parseRulesUrl(searchParams))
  const patch = (p: Partial<RulesUrlState>) => setState(s => ({ ...s, ...p }))
  const [collapsed, setCollapsed] = useSheetCollapsed()
  const [about, setAbout] = useState(false)
  const [jump, setJump] = useState<GroupJump | null>(null)
  const wide = useWide()
  const panel = useFloatingPanel({
    storageKey: 'rules.panel', home: PANEL_HOME, enabled: wide, shape: collapsed ? 'collapsed' : 'open',
  })

  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const k of RULES_URL_CLEARED) next.delete(k)
      writeRulesUrl(state).forEach((v, k) => next.set(k, v))
      return next
    }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const q = useDebounced(state.q, 300)
  const { lang } = useLang()
  const rulesQuery = useQuery({
    queryKey: ['rules', lang, state.region, state.date, state.section, state.kind, state.fishingType,
      state.species, state.gear, state.vesselLength, state.onlyActive, q, state.at, state.radius],
    queryFn: () => cabinetApi.rules({ ...state, q, lang }),
    placeholderData: keepPreviousData,
  })
  useEffect(() => {
    if (state.region && isRegionForbidden(rulesQuery.error)) patch({ region: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesQuery.error])
  const norms = rulesQuery.data?.norms ?? []
  const point = state.at ? rulesQuery.data?.point ?? null : null

  // Точка в бассейне другого каталога: сервер ответил правилами той воды
  // (`region_for_request`), и адрес с заголовком идут за ним.
  const answered = rulesQuery.data?.region
  useEffect(() => {
    if (state.at && answered && answered !== state.region) patch({ region: answered })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered])

  // ---- карта -------------------------------------------------------------
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const gateRef = useRef(createStyleGate())
  const { center, zoom, onMoveEnd } = useMapView()
  const moveEndRef = useRef(onMoveEnd)
  moveEndRef.current = onMoveEnd
  const initialView = useRef({
    center: state.at ? [state.at.lon, state.at.lat] as [number, number]
      : center ?? (home?.lng != null && home.lat != null ? [home.lng, home.lat] as [number, number] : HOME_CENTER),
    zoom: state.at ? 7 : zoom ?? home?.zoom ?? HOME_ZOOM,
  })
  const [box, setBox] = useState<BBox | null>(null)
  const [mapZoom, setMapZoom] = useState(initialView.current.zoom)

  const boundaries = { eez: state.eez, districts: state.fd }
  const boundaryMeta = useBoundaryOverlays({
    mapRef, gateRef, on: boundaries, viewKey: boxKey(box), beforeId: RULES_LAYER_IDS[0],
  })

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      attributionControl: false,
      center: initialView.current.center,
      zoom: initialView.current.zoom,
      // Рамка домена: карта не уводится туда, где сервер откажет.
      maxBounds: domainMaxBounds(),
    })
    mapRef.current = map
    setMapInstance(map)
    const gate = createStyleGate()
    gateRef.current = gate
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.getCanvas().style.cursor = 'crosshair'
    map.on('moveend', () => {
      const c = map.getCenter()
      moveEndRef.current({ lng: c.lng, lat: c.lat, zoom: map.getZoom() })
      setBox(boundsToBox(map))
      setMapZoom(map.getZoom())
    })
    map.on('load', () => {
      applyRulesLayer(map, EMPTY, null)
      applyPointLayer(map, EMPTY)
      setBox(boundsToBox(map))
      gate.markReady()
    })
    // Любой клик — точка: и по воде, и по нарисованному месту. Норму места
    // выбирают в списке, где она стоит первой.
    map.on('click', e => {
      const { lng, lat } = e.lngLat.wrap()
      setState(s => ({ ...s, at: { lat, lon: lng }, radius: clickRadius(map.getZoom(), lat) }))
    })
    const unregisterBasemap = registerBasemapMap(map)
    return () => {
      unregisterBasemap()
      map.remove()
      mapRef.current = null
      setMapInstance(null)
    }
  }, [])

  // Точка — маркером и кругом окрестности: «у края места» в списке иначе не к
  // чему привязать глазами. Радиус — тот, которым считал сервер.
  const radius = point?.radius_m ?? state.radius ?? FALLBACK_RADIUS_M
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const data: GeoJSON.FeatureCollection = state.at
      ? { type: 'FeatureCollection', features: [circlePolygon(state.at.lon, state.at.lat, radius)] }
      : EMPTY
    gateRef.current.run(() => applyPointLayer(map, data))
    if (!state.at) return
    const m = new maplibregl.Marker({ color: POINT_COLOR, scale: 0.7 })
      .setLngLat([state.at.lon, state.at.lat]).addTo(map)
    return () => { m.remove() }
  }, [state.at, radius])

  const drawnCodes = useMemo(
    () => [...new Set(norms.filter(n => n.drawn && n.place_code).map(n => n.place_code!))].sort(),
    [norms],
  )
  const geoQuery = useQuery({
    queryKey: ['rules', 'geo', boxKey(box), Math.round(mapZoom), drawnCodes.join(','), state.date],
    queryFn: () => cabinetApi.rulesGeo({ bbox: box!, zoom: mapZoom, codes: drawnCodes, date: state.date }),
    enabled: box != null && drawnCodes.length > 0,
    placeholderData: keepPreviousData,
  })
  const geo = drawnCodes.length ? (geoQuery.data as unknown as GeoJSON.FeatureCollection | undefined) ?? EMPTY : EMPTY

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => applyRulesLayer(map, geo, state.selected))
  }, [geo, state.selected])

  // Выбранная норма с местом — карта подводится к ней. Лист закрывает часть
  // карты, поэтому отступ — с той стороны, где лист стоит сейчас (его можно
  // отвести): иначе место встало бы ровно под листом.
  const flownRef = useRef<string | null>(null)
  useEffect(() => {
    const map = mapRef.current
    if (!map || !state.selected || flownRef.current === state.selected) return
    const f = geo.features.find(x => x.properties?.code === state.selected)
    const b = f && featureBounds(f)
    if (!b) return
    flownRef.current = state.selected
    const sheet = panel.panelRef.current?.getBoundingClientRect() ?? null
    const frame = map.getContainer().getBoundingClientRect()
    const padding = !wide
      ? { top: 40, right: 40, left: 40, bottom: collapsed ? 80 : Math.round(window.innerHeight * 0.6) }
      : sheetPadding(sheet, frame)
    map.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding, maxZoom: 9, duration: 600 })
  }, [geo, state.selected, collapsed, wide, panel.panelRef])

  const data = rulesQuery.data
  // Смена каталога: слова прежнего приказа и точка сбрасываются, карта уходит
  // на воду выбранного (`catalogSwitch.ts`).
  const onCatalog = (code: string) => {
    patch(switchCatalog(code))
    const home = homeOf(data?.catalogs, code)
    if (home) mapRef.current?.flyTo({ center: home.center, zoom: home.zoom, duration: 600 })
  }
  // Название — текст, за него лист и тянут; смена каталога — стрелка рядом.
  // Растянутый на всю строку выбор каталога съел бы ручку: перетаскивание с
  // поля выбора не начинается (`floatingPanel`).
  const basin = data?.region ?? state.region
  const title = (
    <div className="flex min-w-0 items-center gap-1">
      <h1 className="min-w-0 truncate font-heading text-[15px] font-semibold leading-snug text-foam"
        title={basin ? t(`rules.basin.${basin}`) : undefined}>
        {basin && t(`rules.basin.${basin}`)}
      </h1>
      {data?.catalogs && data.catalogs.length > 1 && (
        <span className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-mist hover:bg-haze hover:text-foam focus-within:outline focus-within:outline-2 focus-within:outline-sonar">
          <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3">
            <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <select
            aria-label={t('rules.catalog')}
            title={t('rules.catalog')}
            className="absolute inset-0 cursor-pointer opacity-0"
            value={data.region}
            onChange={e => onCatalog(e.target.value)}
          >
            {data.catalogs.map(c => (
              <option key={c.code} value={c.code}>{t(`rules.basin.${c.code}`)}</option>
            ))}
          </select>
        </span>
      )}
    </div>
  )
  const controls = (
    <div className="space-y-2">
      <label className="relative block">
        <span className="sr-only">{t('rules.filter.search')}</span>
        <svg aria-hidden viewBox="0 0 16 16" className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-faint">
          <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          className="w-full rounded-lg border border-hull bg-deep py-1.5 pl-7 pr-2 text-[12px] text-foam placeholder:text-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar"
          placeholder={t('rules.filter.search')}
          value={state.q}
          onChange={e => patch({ q: e.target.value })}
        />
      </label>
      <FilterChips state={state} facets={data?.facets} onChange={patch} />
    </div>
  )
  const footer = (
    <div>
      {about && (
        <div className="mb-1.5 space-y-1 text-mist">
          {data?.instruments && Object.entries(data.instruments).map(([code, ins]) => (
            <p key={code}>
              {ins.name}
              {ins.edition && `, ${t('rules.instrument.edition', { date: ins.edition })}`}
              {', '}
              <a href={ins.url} target="_blank" rel="noreferrer" className="underline hover:text-foam">
                {t('rules.instrument.source')}
              </a>
            </p>
          ))}
          {data?.sections_done && <p>{t('rules.sectionsDone')}: {data.sections_done.join(', ')}</p>}
        </div>
      )}
      <div className="flex items-start gap-2">
        <p className="min-w-0 flex-1">{t('rules.disclaimer')}</p>
        <button type="button" aria-expanded={about} onClick={() => setAbout(a => !a)}
          className="shrink-0 rounded px-1 text-mist underline decoration-dotted underline-offset-2 hover:text-foam focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar">
          {t('rules.about')}
        </button>
      </div>
    </div>
  )

  return (
    <div className="relative h-full w-full">
      {/* Высота, а НЕ `absolute inset-0` — сторож `mapContainer.test.ts`. */}
      <div ref={containerRef} className="h-full w-full" />
      <MapGraticule map={mapInstance} />
      <BasemapAttribution corner="br" />

      <RulesSheet collapsed={collapsed} onCollapse={setCollapsed} norms={norms}
        title={title} controls={controls} footer={footer} panel={panel} movable={wide}
        count={t('rules.count', { shown: norms.length, total: data?.total ?? 0 })}
        onJump={key => setJump({ key, at: Date.now() })}>
        {point && <PointFacts point={point} coverage={data?.coverage} onClear={() => patch({ at: null, radius: null })} />}
        {!state.at && (
          <p className="flex items-start gap-2 border-b border-hull px-3 py-2 text-[12px] text-mist">
            <svg aria-hidden viewBox="0 0 16 16" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sonar">
              <circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" strokeWidth="1.3" />
              <path d="M8 1v4M8 11v4M1 8h4M11 8h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <span>{t('rules.point.hint')}</span>
          </p>
        )}
        {rulesQuery.isError && <p className="px-3 py-2 text-[12px] text-coral">{t('rules.error')}</p>}
        {!rulesQuery.isError && data && norms.length === 0 && (
          <p className="px-3 py-2 text-[12px] text-mist">
            {(state.at && coverageNote(data.coverage)) || t('rules.empty')}
          </p>
        )}
        <RuleGroups norms={norms} selected={state.selected} quoteLang={data?.quote_lang} instruments={data?.instruments}
          jump={jump} onSelect={code => patch({ selected: code })} />
      </RulesSheet>

      <div className="pointer-events-none absolute bottom-8 right-3 z-10 max-md:bottom-auto max-md:top-24">
        <div className="pointer-events-auto rounded-xl border border-hull bg-abyss/95 px-3 py-2 text-[12px] shadow-console backdrop-blur">
          <BoundaryToggles on={boundaries}
            onToggle={k => (k === 'eez' ? patch({ eez: !state.eez }) : patch({ fd: !state.fd }))}
            meta={boundaryMeta} />
        </div>
      </div>
    </div>
  )
}
